/**
 * Enterprise SSO is enforced in the database: an access token hook refuses every non-SSO token
 * for an enforced domain (see supabase/schemas/042-workspace-sso.sql). This module holds only the
 * user-facing pieces, so the same sentence appears wherever a flow is diverted or refused, and
 * the tag the hook embeds in its refusal is parsed in one place.
 */

/** Error code the server actions return when a flow cannot continue without SSO. */
export const SSO_REQUIRED_ERROR_CODE = 'sso_required';

/** Shown where the user asked for something passwords would have done: signup, password reset. */
export const SSO_REQUIRED_MESSAGE =
  'Your organization signs in to Geoforge through single sign-on. Use "Sign in with SSO" on the sign-in page to continue.';

/** Shown for the second or so before the browser leaves for the identity provider. */
export const SSO_REDIRECT_NOTICE =
  'Your organization uses single sign-on. Taking you to your company login...';

/** Shown when the company name typed on the SSO step matches no connection. */
export const SSO_NOT_FOUND_MESSAGE =
  "We couldn't find single sign-on for that company. Check the name or ask your administrator.";

/** Shown when the company is known but the handover to the provider could not be started. */
export const SSO_START_FAILED_MESSAGE =
  "We couldn't start your company login. Please try again, or ask your administrator.";

/** Shown when creating a workspace is refused because the organization manages access centrally. */
export const SSO_WORKSPACE_MANAGED_MESSAGE =
  'Your organization manages workspace access through single sign-on. Ask your workspace owner for access.';

/**
 * The hook tags its refusal with the connection's company name, because the message is the only
 * thing GoTrue forwards to our callback. The bound matches the slug's own CHECK constraint, and
 * the strictness is the point: the slug goes straight into a redirect URL.
 */
const SSO_SLUG_TAG_PATTERN = /\[sso:([a-z0-9-]{2,40})\]/;

/** The company name carried by a hook refusal, or null when there is no usable tag. */
export function parseSsoSlugTag(message: string | null | undefined): string | null {
  if (!message) return null;
  return SSO_SLUG_TAG_PATTERN.exec(message)?.[1] ?? null;
}

/**
 * Whether this looks like a refusal from the hook at all, even one whose company name did not
 * survive intact. Enough to tell the user why they were turned away, which is better than the
 * generic sign-in failure, and it still reads only the marker rather than the sentence.
 */
export function hasSsoRefusalMarker(message: string | null | undefined): boolean {
  return Boolean(message?.includes('[sso:'));
}
